
import { ChangeDetectorRef, Component, HostListener, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy, inject, input } from '@angular/core';
import { AllergensIndicatorComponent } from '../AllergensIndicator/AllergensIndicator.component';
import { SmallBadgeComponent } from '../SmallBadge/SmallBadge.component';
import {
  CourseItem,
  QdmService,
  RankItem,
  ServiceItem,
} from 'app/Services/qdm.service';
import { RestaurantHelper } from 'app/Utils/RestaurantHelper';
import { Subscription } from 'rxjs';
import { GestionService } from 'app/Services/gestion.service';
import { ModalCourseDetailsComponent } from '../Modal/ModalCourseDetails/ModalCourseDetails.component';
import { ModalService } from 'app/Services/modal.service';
import { EnvLoader } from 'app/Environment/EnvLoader';

@Component({
    imports: [AllergensIndicatorComponent, SmallBadgeComponent],
    selector: 'week-menu-display',
    changeDetection: ChangeDetectionStrategy.Eager,
    template: `
    <div class="w-full h-full overflow-y-scroll pb-12">
      @if (this.allMenus() && this.currentService()) {
        <table
          class="table-responsive lg:table-fixed w-full h-full bg-white border-collapse border-spacing-0 border border-light"
          >
          <tr class="text-xs outline-dark">
            <th
              class="w-32 font-normal p-2 text-center border border-gris-3 border-t-0 border-l-0"
              >
              Prestation
            </th>
            @for (day of this.weekDays; track day; let index = $index) {
              <th
                [class.border-r-0]="index >= this.weekDays.length - 1"
                class="font-normal p-2 text-right border border-gris-3 border-t-0"
                >
                <p>{{ day.formatDate('fullDay', false) }}</p>
              </th>
            }
          </tr>
          <!-- Création d'une ligne par composante -->
          @for (composante of getUniqueComposantes(); track composante) {
            <tr
              class="border border-gris-3-contour"
              [style.background-color]="this.backgroundForService(composante)"
              >
              <td class="p-2 font-medium text-center border border-gris-3-contour">
                <div
                  class="border border-gris-3-contour m-1 rounded bg-white font-medium text-sm h-fit min-h-32 p-2 flex flex-col"
                  >
                  <div
                    class="w-full h-20 p-4"
                    [style.background-color]="this.backgroundForService(composante)"
                    >
                    <img
                      [attr.src]="this.getIconFor(composante)"
                      class="h-full w-full object-contain"
                      onerror="this.src = 'https://upload.wikimedia.org/wikipedia/commons/a/a3/Image-not-found.png'"
                      alt="Icône de composante"
                      />
                    </div>
                    <p class="flex-1 px-2 pt-2 max-w-full break-words">
                      {{ composante.name }}
                    </p>
                  </div>
                </td>
                @for (day of weekDays; track day; let dayIndex = $index) {
                  <td
                    class="p-2 border border-gris-3-contour align-baseline"
                    [class.border-r-0]="dayIndex >= this.weekDays.length - 1"
                    >
                    @for (
                      menuItem of getMenusForComposanteAndDay(composante, day)
                      ; track
                      menuItem) {
                      <div
                        (click)="this.openModal(menuItem, composante)"
                        class="rounded border cursor-pointer rounded bg-white my-1 border-gris-3-contour h-32 max-h-32 min-h-32 overflow-hidden hover:bg-gris-4 transition-all"
                        >
                        <p
                          class="font-medium min-h-[3.5rem] p-4 fg-noir-2 text-ellipsis overflow-hidden max-h-[3.5rem] leading-5 mb-2"
                          [class.!font-bold]="this.boldCourses()"
                          [class.!uppercase]="this.uppercaseCourses()"
                          [class.!tracking-widest]="this.dyslexie()"
                          style="font-size: 15px;"
                          >
                          {{ menuItem.name }}
                        </p>
                        <div
                          #labelsWrapper
                          class="flex w-full gap-2 px-4 py-0 transition items-center h-[1rem] flex-wrap"
                          style="border-color: #F5F6F7;"
                          >
                          @for (label of menuItem.labels; track label) {
                            <small-badge
                              [backgroundColor]="label.color"
                              [iconUrl]="undefined"
                              [label]="label.name"
                              class="h-4 text-sm"
                              style="font-size: 9px;"
                              />
                          }
                        </div>
                        @if (
                          this.areAllergensDisplayableOnCourses()
                          ) {
                          <div
                            class="flex w-full justify-between py-2 px-2 transition mt-2 items-center"
                            style="border-color: #F5F6F7;"
                            [class.border-t-2]="menuItem.allergens.length > 0"
                            [class.cursor-pointer]="menuItem.allergens.length > 0"
                            [class.hover:bg-gris-4]="menuItem.allergens.length > 0"
                            >
                            @if (menuItem.allergens.length > 0) {
                              <p
                                class="text-xs font-medium block"
                                >
                                {{ 'Allergènes (' + menuItem.allergens.length + ')' }}
                              </p>
                            }
                            @if (menuItem.allergens.length > 0) {
                              <allergens-indicator
                  [isAllergenPresent]="
                    this.hasMarkedAllergen(menuItem.allergens)
                  "
                                class="block"
                                />
                            }
                          </div>
                        }
                      </div>
                    }
                  </td>
                }
              </tr>
            }
          </table>
        }
      </div>
    `
})
export class WeekMenuDisplayComponent implements OnInit, OnChanges, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private qdm = inject(QdmService);
  private gestion = inject(GestionService);
  private modal = inject(ModalService);


  private readonly ALT_BACKGROUND_COLORS = [
    '#E5F5FC',
    '#FEEFD6',
    '#FEE5ED',
    '#F0F9F0',
  ];

  public readonly selectedDate = input.required<Date | undefined>();

  public readonly currentService = input.required<ServiceItem | undefined>();

  public readonly allMenus = input.required<ServiceItem[] | undefined>();

  public readonly markedAllergens = input.required<number[]>();

  readonly uppercaseCourses = input.required<boolean>();

  readonly dyslexie = input.required<boolean>();

  readonly boldCourses = input.required<boolean>();

  readonly showWeekEnd = input.required<boolean>();

  protected weekDays: Date[] = [];

  protected restaurantHelper: RestaurantHelper | undefined = undefined;

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['restaurant'] ||
      changes['selectedDate'] ||
      changes['currentService']
    ) {
      this.populateWeekDays();
    }
  }

  sub: Subscription = new Subscription();

  ngOnInit(): void {
    this.checkForMobile();
    this.populateWeekDays();
    this.sub.add(
      this.qdm.selectedRestaurant$.subscribe((restaurant) => {
        if (restaurant) {
          this.qdm.fetchRestaurantById(restaurant.id).then((res) => {
            this.restaurantHelper = new RestaurantHelper(res);
            this.cdr.detectChanges();
          });
        }
      })
    );

    this.sub.add(
      this.qdm.currentService$.subscribe((svc) => {
        // quand le service change on refresh
        if (!svc) {
          this.iconsServices = [];
          return;
        }

        const idsNotNull = svc.components.map((c) => c.family_id || c.id);
        this.gestion.fetchServicesIcones(idsNotNull).then((icones) => {
          this.iconsServices = icones;
          this.cdr.detectChanges();
        });
      })
    );
  }

  iconsServices: { [key: number]: { url: string; bg: string } } = {};
  populateWeekDays() {
    const selectedDate = this.selectedDate();
    if (!selectedDate) {
      return;
    }

    const startOfWeek = new Date(selectedDate);
    startOfWeek.setDate(
      selectedDate.getDate() - selectedDate.getDay() + 1
    ); // Lundi

    if (this.showWeekEnd()) {
      this.weekDays = Array.from({ length: 7 }, (_, index) => {
        const day = new Date(startOfWeek);
        day.setDate(startOfWeek.getDate() + index);
        return day;
      });
    } else {
      this.weekDays = Array.from({ length: 5 }, (_, index) => {
        const day = new Date(startOfWeek);
        day.setDate(startOfWeek.getDate() + index);
        return day;
      });
    }

    this.cdr.detectChanges();
  }

  // Méthode pour obtenir toutes les composantes uniques
  getUniqueComposantes(): RankItem[] {
    const allMenus = this.allMenus();
    if (!allMenus || allMenus.length === 0) {
      return [];
    }

    // Utiliser un Map pour garder une trace des composantes uniques par ID
    const uniqueComposantesMap = new Map<number, RankItem>();

    // Filtrer pour n'avoir que les services avec le même svc_id que le service courant
    const relevantMenus = allMenus.filter(
      (menu) => menu.svc_id === this.currentService()?.svc_id
    );

    // Extraire toutes les composantes de ces menus
    relevantMenus.forEach((menu) => {
      if (menu.components) {
        menu.components.forEach((comp) => {
          if (!uniqueComposantesMap.has(comp.id)) {
            uniqueComposantesMap.set(comp.id, comp);
          }
        });
      }
    });

    return Array.from(uniqueComposantesMap.values());
  }

  // Méthode pour obtenir les menus d'une composante pour un jour spécifique
  getMenusForComposanteAndDay(composante: RankItem, day: Date): any[] {
    const allMenus = this.allMenus();
    if (!allMenus || allMenus.length === 0 || !this.currentService()) {
      return [];
    }

    const dayString = this.formatDateToString(day);

    // Filtrer les menus pour le jour spécifique et le service courant
    // Modification: utiliser le jour de la semaine pour s'assurer que le menu correspond exactement au jour affiché
    return allMenus
      .filter((menu) => {
        const menuDate = new Date(Date.parse(menu.day_start));
        return (
          this.formatDateToString(menuDate) === dayString &&
          menu.svc_id === this.currentService()?.svc_id
        );
      })
      .flatMap((menu) => {
        if (!menu.components) return [];

        // Rechercher par ID de composante plutôt que par nom
        const compItem = menu.components.find(
          (comp) => comp.id === composante.id
        );

        if (!compItem) return [];

        return compItem.courses || [];
      });
  }

  // Méthode pour formater une date en string compatible avec day_start
  private formatDateToString(date: Date): string {
    return date.formatDate('forApi');
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  hasMarkedAllergen(
    allergens: { id: number; name: string; correspondance?: number }[]
  ): boolean {
    const markedAllergens = this.markedAllergens();
    return allergens.some(
      (allergen) =>
        markedAllergens.includes(allergen.id) ||
        (allergen.correspondance &&
          markedAllergens.includes(allergen.correspondance))
    );
  }

  backgroundForService(rank: RankItem): string {
    return this.ALT_BACKGROUND_COLORS[
      rank.id % this.ALT_BACKGROUND_COLORS.length
    ];
  }

  public static readonly IMAGE_NOT_FOUND_URL: string =
    'https://upload.wikimedia.org/wikipedia/commons/a/a3/Image-not-found.png';

  private readonly icones = [
    {
      names: [
        'entree',
        'entrees',
        'entre',
        'entrée',
        'entrée 1',
        'entrée 2',
        'entrée 3',
        'entrée alternative',
        'entrée de substitution',
        'entrée substitution',
        'entrées',
        'enrées',
        "hors d'oeuvre",
        'purée de légumes',
      ],
      path: 'Entrée.png',
      color: 'B9FBC0',
    },
    {
      names: [
        'dessert',
        'desserts',
        'dessert 2',
        'dessert sup.',
        'dessert/fromage',
        'desserts lactés',
        'pâtisserie',
        'biscuit',
        'yaourt',
      ],
      path: 'Dessert.png',
      color: 'F1C0E8',
    },
    {
      names: ['boisson', 'boissons', 'boisson chaude', 'eau'],
      path: 'Eau.png',
      color: '90DBF4',
    },
    {
      names: [
        'fromage',
        'fromages',
        'fromage 2',
        'fromage bio',
        'fromages et laitage',
        'fromage et laitages',
        'fromages / laitages',
        'fromages et laitages',
        'laitage ou fromage',
      ],
      path: 'Fromage.png',
      color: 'FBF8CC',
    },
    {
      names: [
        'laitage',
        'produit laitier',
        'produits laitiers',
        'produit laitier ou divers',
      ],
      path: 'Lait.png',
      color: 'A3C4F3',
    },
    {
      names: ['fruit', 'fruit bio', 'purée de fruits', 'fruits'],
      path: 'Fruit.png',
      color: 'CFBAF0',
    },
    {
      names: [
        'legume',
        'legumes',
        'vegetarien',
        'vegetariens',
        'féculents ou légumes cuits',
        'deuxième légume',
        'salades',
      ],
      path: 'Legume.png',
      color: 'FFCFD2',
    },
    {
      names: [
        'viande',
        'viandes',
        'proteines',
        'proteine',
        'grill',
        'purée de légumes et viande',
        'grillade',
        'grillades',
        'protéine',
      ],
      path: 'Viande-1.png',
      color: 'FDE4CF',
    },
    {
      names: ['pain', 'pains'],
      path: 'Pain.png',
      color: 'FDE4CF',
    },
    {
      names: ['potage', 'potages', 'soupe', 'soupes', 'soupers', 'poatge'],
      path: 'Soupe.png',
      color: 'FFCFD2',
    },
    {
      names: [
        'plat',
        'plat 2',
        'plat alternatif',
        'plat complet',
        'plat de fête',
        'plat de substitution',
        'plat de subtitution',
        'plat du jour',
        'plat froid',
        'plat principal',
        'plat protéiné',
        'plat régime',
        'plat spécial',
        'plat substitution',
        'plats',
        'plats chauds',
        'plats protidiques',
        'menu',
        'composante unique',
      ],
      path: 'Assiette-complete.png',
      color: 'FDE4CF',
    },
    {
      names: ['divers', 'autre'],
      path: 'Divers.png',
      color: 'FDE4CF',
    },
    {
      names: [
        'accompagnement',
        'accompagnement 1',
        'accompagnement 2',
        'accompagnements',
        'divers',
        'garniture',
        'garniture 1',
        'garniture 2',
        'garnitures',
        'granitures',
        'féculent',
        'deuxième féculent',
        'choix 2',
        'collation',
        'composante 1',
        'composante 2',
        'composante 3',
        'composante 4',
        'composition 1',
        'composition 2',
        'composition 3',
        'dîner équilibré',
        'frais',
        'sans viande',
        'substitution',
        'suggestion diner',
        'suggestion du soir',
        'suggestion pour le soir',
        'dîner équilibré',
        'frais',
      ],
      path: 'Assiette-complete-2.png',
      color: 'FFCFD2',
    },
    {
      names: ['poisson'],
      path: 'Poisson-1.png',
      color: 'F1C0E8',
    },
    {
      names: ['sandwich ou crudité', 'sandwich ou salade', 'sandwiches'],
      path: 'Sandwich.png',
      color: 'FFCFD2',
    },
    {
      names: ['pizza', 'paninis', 'chips', 'snacking'],
      path: 'Snaking.png',
      color: 'CFBAF0',
    },
    {
      names: [
        'gouter',
        'gouters',
        'goûter',
        'goûters',
        'viennoiserie',
        'vienoiserie',
      ],
      path: 'Gouter.png',
      color: 'FFCFD2',
    },
    {
      names: ['petit déjeuner'],
      path: 'Petit-dej.png',
      color: 'FDE4CF',
    },
    {
      names: ['picnic'],
      path: 'Picnic.png',
      color: 'FFCFD2',
    },
  ];

  getIconFor(rank: RankItem): string {
    const icon = this.iconsServices[rank.family_id || -1];
    if (icon) {
      return icon.url;
    }

    let name = rank.name.toLowerCase();
    // replace accents with letter without accents
    name = name.replace(/[éèê]/g, 'e');
    // replace uppercase accents
    name = name.replace(/À/g, 'A');
    name = name.replace(/É/g, 'E');
    name = name.replace(/Ç/g, 'C');
    name = name.replace(/Ô/g, 'O');
    name = name.replace(/Î/g, 'I');

    name = name.replace(/à/g, 'a');
    name = name.replace(/é/g, 'e');
    name = name.replace(/è/g, 'e');
    name = name.replace(/ê/g, 'e');
    name = name.replace(/ç/g, 'c');
    name = name.replace(/ô/g, 'o');
    name = name.replace(/î/g, 'i');

    name = name.trim().toLowerCase();

    const boGestion = EnvLoader.load().boGestionUrl;

    for (let i = 0; i < this.icones.length; i++) {
      const icone = this.icones[i];

      for (let j = 0; j < icone.names.length; j++) {
        const n = icone.names[j].toLowerCase().trim();
        if (name.includes(n) || n.includes(name)) {
          return `${boGestion}/img/menu_icons/${icone.path}`;
        }
      }
    }

    return WeekMenuDisplayComponent.IMAGE_NOT_FOUND_URL;
  }

  openModal(item: CourseItem, rank: RankItem) {
    this.modal.open(
      ModalCourseDetailsComponent,
      {
        course: item,
        service: this.currentService(),
        restaurantId: this.currentService()?.rst_id || 0,
        childId: -1,
        date: this.selectedDate(),
        rank: rank,
        restaurantHelper: this.restaurantHelper,
        mode: 'week',
        iconsServices: this.iconsServices,
      },
      () => {}
    );
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.checkForMobile();
  }

  checkForMobile() {
    if (window.innerWidth < 900) {
      const customer = this.qdm.currentCustomerSubject.value;
      const env = EnvLoader.load();

      if (customer && env) {
        const link = `${env.qdmApiUrl}/apps/?app=${encodeURIComponent(
          customer.APP_NAME
        )}`;
        window.location.assign(link);
      }
    }
  }

  areAllergensDisplayableOnCourses(): any {
    if(!this.restaurantHelper) return true;
  
    return this.restaurantHelper.areAllergensDisplayableOnCourses();
  }
  
}
